// ============================================================
//  Gate-Befund — Risiken, Konfidenz und Freigabeempfehlung je Landschaft AUS DEN DATEN.
//  Jede Aussage ist eine Regel über eine Zahl (Countdown, Anteile, Vertragsstand); nichts
//  ist Freitext aus einer Tabelle. Schwellen stehen als Konstanten hier, mit Herleitung im
//  Ergebnis, damit die Seite sie als Rechenweg zeigen kann.
// ============================================================
import type { Dataset } from "../data/generator";
import type { Landscape } from "./types";
import { DEFAULT_RATIO, daysUntil, formatFue, formatNum, formatPct, formatTage, todayIso, type RatioTable } from "./rules";
import { assignedLandscapes, landscapeById } from "../data/landscapes";
import { isFueRelevantLandscape, landscapeLicensing, openEngineAssignments } from "../data/licensing";
import { contractedFue } from "../store/contract";
import { MODEL_LABEL } from "./licensing";
import { landscapeRisk, leverStats } from "./aggregate";
import { gateChain, type GateForecast } from "./analysis";

export type Schwere = "rot" | "gelb";

/** Ein Risiko (oder Hebel) mit der Zahl, aus der es folgt. */
export interface GateRisiko {
  id: "countdown" | "ueberberechtigt" | "ohne-trace" | "engine-offen" | "vertragsluecke" | "metrik-offen";
  schwere: Schwere;
  /** Kurzname für Liste und PDF, z. B. „Vorlauf < 60 Tage". */
  label: string;
  /** Die Zahl, formatiert: „41 Tage", „34 %", „Lücke 2.100 FUE". */
  wert: string;
  /** Regel und Schwelle in einem Satz — für den InfoTip. */
  herleitung: string;
}

/** Chance, kein Risiko: großer vermeidbarer Anteil am Gate-Bedarf. */
export interface GateHebel {
  anteil: number;
  avoidable: number;
  fueAdded: number;
  label: string;
  wert: string;
  herleitung: string;
}

export interface GateKonfidenz {
  /** 0..100, gewichtet aus Trace- und Schlüsselabdeckung. */
  prozent: number;
  konten: number;
  kontenMitTrace: number;
  kontenMitPersonalnummer: number;
  traceAbdeckung: number;
  schluesselAbdeckung: number;
  gewichtTrace: number;
  gewichtSchluessel: number;
  herleitung: string;
}

export interface GateMassnahme {
  leverId: string;
  /** Technischer Rollenname. */
  roleName: string;
  roleId: string;
  sid: string;
  authObject: string;
  deltaFue: number;
  effective: number;
}

export type EmpfehlungStufe = "freigabe" | "auflage" | "keine";

export interface GateBefund {
  landscapeId: string;
  fueRelevant: boolean;
  gate?: GateForecast;
  daysLeft: number | null;
  risiken: GateRisiko[];
  hebel: GateHebel | null;
  groessteMassnahme: GateMassnahme | null;
  konfidenz: GateKonfidenz;
  empfehlung: { stufe: EmpfehlungStufe; text: string };
}

export const BEFUND_SCHWELLEN = {
  countdownTage: 60,
  ueberberechtigtRot: 0.3,
  ueberberechtigtGelb: 0.2,
  ohneTrace: 0.1,
  hebelAnteil: 0.25,
  gewichtTrace: 0.5,
  gewichtSchluessel: 0.5,
} as const;

function konfidenzFor(ds: Dataset, sids: Set<string>): GateKonfidenz {
  const konten = ds.accounts.filter((a) => sids.has(a.sid));
  const n = konten.length;
  const mitTrace = konten.filter((a) => a.trace !== "kein-trace").length;
  const mitPnr = konten.filter((a) => !!a.personalnummer).length;
  const trace = n ? mitTrace / n : 0;
  const key = n ? mitPnr / n : 0;
  const { gewichtTrace, gewichtSchluessel } = BEFUND_SCHWELLEN;
  const prozent = n ? Math.round((gewichtTrace * trace + gewichtSchluessel * key) * 100) : 0;
  return {
    prozent, konten: n, kontenMitTrace: mitTrace, kontenMitPersonalnummer: mitPnr, traceAbdeckung: trace, schluesselAbdeckung: key,
    gewichtTrace, gewichtSchluessel,
    herleitung: n
      ? `Trace-Abdeckung ${formatNum(mitTrace)} von ${formatNum(n)} Konten = ${formatPct(trace * 100, 0)} × ${gewichtTrace} + Schlüsselabdeckung (Personalnummer) ${formatNum(mitPnr)} von ${formatNum(n)} = ${formatPct(key * 100, 0)} × ${gewichtSchluessel} = ${formatPct(prozent, 0)}`
      : "Keine Konten in den Systemen dieser Landschaft — keine Konfidenz ableitbar.",
  };
}

function groessteMassnahme(ds: Dataset, landscapeId: string, ratio: RatioTable): GateMassnahme | null {
  const levers = ds.levers.filter((l) => l.landscapeId === landscapeId);
  if (!levers.length) return null;
  const stats = leverStats(levers, ds.persons, ratio);
  let best: (typeof stats)[number] | null = null;
  for (const s of stats) if (!best || s.deltaFue > best.deltaFue) best = s;
  if (!best || best.deltaFue <= 0) return null;
  const lv = levers.find((l) => l.id === best!.id)!;
  return { leverId: lv.id, roleName: lv.roleName, roleId: lv.roleId, sid: lv.sid, authObject: lv.authObject, deltaFue: best.deltaFue, effective: best.effective };
}

/**
 * Befund eines Gates. `landscapes` sind die wirksamen Landschaften (editierter Cutover-Plan),
 * `contracted` die Vertragszahl (Standard: Vertragsgrundlage aus dem Store), `today` der Stichtag
 * für den Countdown (Standard: heute — Tests geben ein festes Datum mit).
 */
export function gateBefund(
  landscapeId: string,
  ds: Dataset,
  ratio: RatioTable = DEFAULT_RATIO,
  landscapes: readonly Landscape[] = assignedLandscapes,
  contracted: number = contractedFue(ratio),
  today: string = todayIso(),
): GateBefund {
  const S = BEFUND_SCHWELLEN;
  const l = landscapes.find((x) => x.id === landscapeId) ?? landscapeById(landscapeId);
  const fueRelevant = isFueRelevantLandscape(landscapeId);
  const gate = gateChain(ds, ratio, landscapes).find((g) => g.landscapeId === landscapeId);
  const daysLeft = daysUntil(l?.cutover, today);
  const sids = new Set(l?.systems ?? []);
  const risiken: GateRisiko[] = [];

  // Termin: weniger als 60 Tage Vorlauf (K2) — gilt für jedes Gate, auch Engine/HCM.
  if (daysLeft !== null && daysLeft >= 0 && daysLeft < S.countdownTage) {
    risiken.push({
      id: "countdown", schwere: "rot", label: `Vorlauf < ${S.countdownTage} Tage`, wert: formatTage(daysLeft),
      herleitung: `Cutover in ${formatTage(daysLeft)} — unter ${S.countdownTage} Tagen ist ein Rückbau vor dem Gate nicht mehr realistisch (K2).`,
    });
  }

  const sysLic = landscapeLicensing(landscapeId);
  const annahmen = sysLic.filter((s) => !s.belegt);
  const engineOffen = openEngineAssignments.filter((o) => [...sids].some((sid) => o.status.includes(sid)));

  if (!fueRelevant) {
    // Engine-/HCM-Gate: Es gibt keine FUE-Risiken. Offen ist die Metrik selbst.
    const ohneWert = sysLic.filter((s) => s.engine && s.engine.wert === null);
    if (ohneWert.length) {
      risiken.push({
        id: "metrik-offen", schwere: "rot", label: "Metrik offen",
        wert: ohneWert.map((s) => `${s.sid}: ${s.engine!.name}`).join(", "),
        herleitung: `${formatNum(ohneWert.length)} von ${formatNum(sysLic.length)} Systemen mit eigener Metrik ohne Vertragswert (${ohneWert.map((s) => s.engine!.einheit).join("; ")}). Ohne Wert ist der Bedarf am Gate nicht bewertbar.`,
      });
    }
  } else {
    // Über-Berechtigung: Anteil Personen mit vermeidbarem Über-Grant in dieser Landschaft.
    const r = landscapeRisk(ds.persons)[landscapeId];
    if (r && r.persons > 0 && r.share >= S.ueberberechtigtGelb) {
      const rot = r.share >= S.ueberberechtigtRot;
      risiken.push({
        id: "ueberberechtigt", schwere: rot ? "rot" : "gelb", label: rot ? "Über-Berechtigung kritisch" : "Über-Berechtigung beobachten",
        wert: formatPct(r.share * 100, 0),
        herleitung: `${formatNum(r.lifted)} von ${formatNum(r.persons)} Personen mit Bezug tragen einen vermeidbaren Über-Grant = ${formatPct(r.share * 100, 0)} (Schwelle: ≥ ${formatPct(S.ueberberechtigtRot * 100, 0)} kritisch, ≥ ${formatPct(S.ueberberechtigtGelb * 100, 0)} beobachten).`,
      });
    }

    // Personen ohne Trace-Aufzeichnung in den Systemen dieser Landschaft.
    const accById = new Map(ds.accounts.map((a) => [a.id, a]));
    let mitBezug = 0, ohneTrace = 0;
    for (const p of ds.persons) {
      if (!p.landscapeIds.includes(landscapeId)) continue;
      const konten = p.accountIds.map((id) => accById.get(id)).filter((a) => a && sids.has(a.sid));
      if (!konten.length) continue;
      mitBezug++;
      if (konten.every((a) => a!.trace === "kein-trace")) ohneTrace++;
    }
    const ohneTraceAnteil = mitBezug ? ohneTrace / mitBezug : 0;
    if (ohneTraceAnteil > S.ohneTrace) {
      risiken.push({
        id: "ohne-trace", schwere: "gelb", label: "Ohne Trace-Aufzeichnung", wert: formatPct(ohneTraceAnteil * 100, 0),
        herleitung: `${formatNum(ohneTrace)} von ${formatNum(mitBezug)} Personen haben in ${[...sids].join(", ")} kein Konto mit Workload-Statistik = ${formatPct(ohneTraceAnteil * 100, 0)} (Schwelle > ${formatPct(S.ohneTrace * 100, 0)}). „Nie genutzt" ist für sie nicht belegbar.`,
      });
    }

    // Vertragslücke nach dem Gate.
    if (gate && gate.cumulative > contracted) {
      risiken.push({
        id: "vertragsluecke", schwere: "rot", label: "Vertragslücke nach dem Gate", wert: `Lücke ${formatFue(gate.cumulative - contracted)}`,
        herleitung: `Kumuliert nach dem Gate ${formatFue(gate.cumulative)} > Vertrag ${formatFue(contracted)}. Mit Optimierung dieses Systems: ${formatFue(gate.cumulativeOptimized)}.`,
      });
    }
  }

  // Engine-Zuordnung offen (Annahme statt Beleg) — betrifft FUE- wie Engine-Landschaften.
  if (annahmen.length || engineOffen.length) {
    const teile = [
      ...annahmen.map((s) => `${s.sid} (${MODEL_LABEL[s.model]}, Annahme)`),
      ...engineOffen.map((o) => `${o.engine} als Annahme auf ${[...sids].find((sid) => o.status.includes(sid))}`),
    ];
    risiken.push({
      id: "engine-offen", schwere: "gelb", label: "Engine-/Lizenzzuordnung offen", wert: `${formatNum(annahmen.length + engineOffen.length)} offen`,
      herleitung: `${teile.join("; ")}. Ändert sich die Einstufung, ändert sich der FUE-Bedarf dieses Gates.`,
    });
  }

  // Hebel: großer vermeidbarer Anteil — eine Chance, kein Risiko.
  let hebel: GateHebel | null = null;
  if (fueRelevant && gate && gate.fueAdded > 0) {
    const anteil = gate.avoidable / gate.fueAdded;
    if (anteil > S.hebelAnteil) {
      hebel = {
        anteil, avoidable: gate.avoidable, fueAdded: gate.fueAdded, label: "Hebel: vermeidbarer Anteil", wert: formatPct(anteil * 100, 0),
        herleitung: `${formatFue(gate.avoidable)} von ${formatFue(gate.fueAdded)} Gate-Bedarf sind vermeidbar = ${formatPct(anteil * 100, 0)} (Schwelle > ${formatPct(S.hebelAnteil * 100, 0)}). Rückbau vor dem Cutover senkt den Stand nach dem Gate auf ${formatFue(gate.cumulativeOptimized)}.`,
      };
    }
  }

  const konfidenz = konfidenzFor(ds, sids);
  const massnahme = fueRelevant ? groessteMassnahme(ds, landscapeId, ratio) : null;

  // Empfehlung aus der Schwere — ein Satz, aus Regeln.
  const rot = risiken.filter((r) => r.schwere === "rot");
  const gelb = risiken.filter((r) => r.schwere === "gelb");
  const empfehlung: GateBefund["empfehlung"] = rot.length
    ? { stufe: "keine", text: `Keine Freigabe: ${rot.map((r) => `${r.label} (${r.wert})`).join("; ")}.` }
    : gelb.length
      ? { stufe: "auflage", text: `Freigabe unter Auflage: ${gelb.map((r) => `${r.label} (${r.wert})`).join("; ")}.` }
      : { stufe: "freigabe", text: gate && fueRelevant ? `Freigabe: keine roten Risiken, kumuliert ${formatFue(gate.cumulative)} ≤ Vertrag ${formatFue(contracted)}.` : "Freigabe: keine roten Risiken." };

  return { landscapeId, fueRelevant, gate, daysLeft, risiken, hebel, groessteMassnahme: massnahme, konfidenz, empfehlung };
}
